// PJC v10 — état d'achat partagé (window.PJC_PURCHASE), choix de taille obligatoire, soumission unique
// (bouton principal, barre fixe, packs), états chargement / confirmation / erreur, annonces vocales.
//
// Événements (tous sur document) :
//   écoutés  : pjc:offer-state (Offres), pjc:purchase-submit (bouton principal / barre fixe),
//              pjc:buy-state (rapports de product-form.js), pjc:variant-lookup (product-form.js)
//   appelés  : window.PJC_OFFERS.add() (soumission d'un pack), window.PJC_OFFERS.focusMissing()
//   émis     : pjc:purchase-change, pjc:buy-state (diffusion, detail.broadcast), pjc:cart-updated

const EVENTS = {
  change: 'pjc:purchase-change',
  submit: 'pjc:purchase-submit',
  offer: 'pjc:offer-state',
  state: 'pjc:buy-state',
  cartUpdated: 'pjc:cart-updated',
  lookup: 'pjc:variant-lookup',
};

const SUCCESS_DURATION = 1800;
const MESSAGE_DURATION = 10000;
const HIGHLIGHT_DURATION = 1600;
const INERT_CONTEXT = 'quick-add-dialog, .quick-add-modal, product-card, dialog';

const TEXT = {
  add: 'Ajouter au panier',
  unavailable: 'Actuellement indisponible',
  chooseVariant: 'Choisissez une variante',
  loading: 'Ajout en cours…',
  added: 'Ajouté au panier',
  network: 'Connexion impossible, réessayez.',
  generic: 'Une erreur est survenue, réessayez.',
  packUnavailable: "Cette offre n'est pas disponible pour le moment, réessayez.",
  packNoExpress: "Le paiement express n'est pas disponible pour un pack : ajoutez-le au panier.",
};

/** @param {string} name */
function guessKind(name) {
  const value = String(name || '').toLowerCase();
  if (/size|taille/.test(value)) return 'size';
  if (/colou?r|couleur|teinte|coloris/.test(value)) return 'color';
  return 'other';
}

/**
 * @param {string} rawName
 * @param {string} kind
 */
function frenchLabel(rawName, kind) {
  if (kind === 'size') return 'Taille';
  if (kind === 'color') return 'Couleur';
  return String(rawName || '').trim() || 'Option';
}

/** @param {string} label */
function phrase(label) {
  const kind = guessKind(label);
  if (kind === 'size') return 'une taille';
  if (kind === 'color') return 'une couleur';
  return `l’option « ${label} »`;
}

/** @param {string[]} labels */
function chooseMessage(labels) {
  return `Choisissez ${labels.map(phrase).join(' et ')}`;
}

/** @type {Intl.NumberFormat | null | undefined} undefined : pas encore créé ; null : Intl indisponible */
let moneyFormatter;

// Espace insécable entre le code devise et le montant (écrite sans séquence d'échappement).
const NBSP = String.fromCharCode(160);

/**
 * Même présentation que la boutique et pjc-offers.js : « CHF 39.90 » (code devise, espace insécable, deux décimales,
 * sans séparateur de milliers). Le format monétaire du thème (data-money-format) peut contenir du HTML (<span class="money">)
 * ou une autre devise : il ne sert que de repli si Intl est indisponible, balises retirées.
 * @param {number} cents
 * @param {string} format - format monétaire de la boutique, ex. « CHF {{amount}} »
 * @param {string} currency - code ISO de la devise active
 */
function formatMoney(cents, format, currency) {
  const value = Math.round(Number(cents) || 0) / 100;

  if (moneyFormatter === undefined) {
    try {
      moneyFormatter = new Intl.NumberFormat('en', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
    } catch {
      moneyFormatter = null;
    }
  }
  if (moneyFormatter) return `${currency}${NBSP}${moneyFormatter.format(value)}`;

  const plain = String(format || '').replace(/<[^>]*>/g, '').replace(/{{\s*(\w+)\s*}}/g, (_, token) =>
    token === 'currency' ? currency : value.toFixed(2)
  );
  return /\d/.test(plain) ? plain.trim() : `${currency}${NBSP}${value.toFixed(2)}`;
}

/** @param {unknown} value */
function toCents(value) {
  const number = Math.round(Number(value));
  return Number.isFinite(number) && number > 0 ? number : 0;
}

/** @param {Element | null} element */
function inViewport(element) {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0;
}

/* --- Annonces vocales (créées une seule fois) -------------------------------------------------- */

/** @type {{polite: HTMLElement, assertive: HTMLElement} | null} */
let liveRegions = null;

function ensureLiveRegions() {
  if (liveRegions && liveRegions.polite.isConnected) return liveRegions;

  const make = (role, level) => {
    const element = document.createElement('div');
    element.className = 'pjc-live';
    element.setAttribute('role', role);
    element.setAttribute('aria-live', level);
    element.setAttribute('aria-atomic', 'true');
    document.body.appendChild(element);
    return element;
  };

  liveRegions = { polite: make('status', 'polite'), assertive: make('alert', 'assertive') };
  return liveRegions;
}

/**
 * @param {string} text
 * @param {'polite' | 'assertive'} level
 */
function announce(text, level) {
  const region = ensureLiveRegions()[level];
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = text;
  }, 40);
  window.setTimeout(() => {
    if (region.textContent === text) region.textContent = '';
  }, 6000);
}

/* --- Message flottant de secours (aucun emplacement d'erreur visible à l'écran) ---------------- */

/** @type {number | undefined} */
let toastTimer;

/**
 * @param {string} message
 * @param {'error' | 'notice'} kind
 */
function showToast(message, kind) {
  let toast = /** @type {HTMLElement | null} */ (document.querySelector('.pjc-toast'));
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'pjc-toast';
    toast.setAttribute('aria-hidden', 'true');
    toast.setAttribute('data-pjc-cart-open-hide', '');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.dataset.kind = kind;
  toast.hidden = false;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    if (toast) toast.hidden = true;
  }, 4500);
}

/** @type {PjcBuy | null} */
let active = null;

class PjcBuy extends HTMLElement {
  #abort = new AbortController();

  /** @type {{id: number, a: boolean, p: number, c: number, o: string[], t: string}[]} */
  #variants = [];
  #rawVariants = '';

  /** @type {string[]} */
  #optionNames = [];

  /** @type {Set<string>} noms bruts des options dont le choix a été fait explicitement */
  #confirmed = new Set();

  /** Vrai tant qu'au moins une combinaison disponible reste possible malgré les choix manquants. */
  #choosable = true;

  /** @type {any} dernier état reçu de pjc:offer-state quand mode ≠ single */
  #offer = null;

  /** @type {any} variante renvoyée par la dernière mise à jour de sélection (repli sans table) */
  #resource = null;

  /** @type {'idle' | 'loading' | 'success'} */
  #state = 'idle';

  /** @type {'main' | 'sticky'} */
  #source = 'main';

  /** @type {'main' | 'sticky' | null} */
  #pendingSource = null;

  #lastKey = '';
  #initialised = false;
  #scheduled = false;
  #burstStart = 0;
  #burstCount = 0;
  #message = '';

  /** @type {'error' | 'notice'} */
  #messageKind = 'error';
  #lastMissing = '';

  /** @type {number | undefined} */
  #successTimer;

  /** @type {number | undefined} */
  #messageTimer;

  /** @type {number | undefined} */
  #highlightTimer;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {Document | Element} */
  #root = document;

  connectedCallback() {
    if (this.closest(INERT_CONTEXT)) return;

    // Élément déplacé puis reconnecté (morph) : nouveau cycle de vie complet.
    if (this.#abort.signal.aborted) {
      this.#abort = new AbortController();
      this.#initialised = false;
    }

    active = this;
    this.#root = this.closest('.shopify-section') ?? document;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(), { once: true, signal: this.#abort.signal });
    } else {
      this.#init();
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    window.clearTimeout(this.#successTimer);
    window.clearTimeout(this.#messageTimer);
    window.clearTimeout(this.#highlightTimer);
    if (active === this) active = null;
  }

  #init() {
    if (this.#initialised || !this.isConnected) return;
    this.#initialised = true;

    const { signal } = this.#abort;
    this.#readTable();

    // Choix explicite déjà présent dans l'URL (lien partagé, rechargement) : tout est confirmé.
    const variantParam = new URL(window.location.href).searchParams.get('variant');
    if (variantParam && (this.#variants.length === 0 || this.#variants.some((v) => String(v.id) === variantParam))) {
      for (const fieldset of this.#fieldsets()) this.#confirmed.add(fieldset.rawName);
    }

    this.#root.addEventListener('change', this.#onChange, { signal });
    this.#root.addEventListener('input', this.#onQuantity, { signal });
    this.#root.addEventListener('quantity-selector:update', this.#onQuantity, { signal });
    this.#root.addEventListener('shopify:product:select', this.#onProductSelect, { signal });
    document.addEventListener(EVENTS.submit, this.#onSubmit, { signal });
    document.addEventListener(EVENTS.offer, this.#onOffer, { signal });
    document.addEventListener(EVENTS.state, this.#onReport, { signal });
    document.addEventListener(EVENTS.lookup, this.#onLookup, { signal });

    this.#watch();
    ensureLiveRegions();
    this.refresh();
  }

  /** Les morphs de Horizon (sélecteur de variante, boutons) effacent nos attributs : on les rétablit. */
  #watch() {
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => this.#schedule());

    for (const node of [this.parentElement, ...this.#pickers()]) {
      if (!node) continue;
      this.#observer.observe(node, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-pjc-unconfirmed', 'data-pjc-state', 'aria-busy', 'checked', 'inert', 'hidden'],
      });
    }
  }

  /**
   * Replanifie un rendu après une mutation du DOM. Un passage par tâche (jamais par microtâche) et un
   * disjoncteur : si les mutations ne se calment pas, l'observateur est coupé plutôt que de figer la page.
   */
  #schedule() {
    if (this.#scheduled) return;

    const now = Date.now();
    if (now - this.#burstStart > 1000) {
      this.#burstStart = now;
      this.#burstCount = 0;
    }
    if (++this.#burstCount > 40) {
      this.#observer?.disconnect();
      window.setTimeout(() => {
        if (!this.isConnected || !this.#initialised) return;
        // Réarmement : les mutations survenues pendant la pause n'ont pas été vues, on relit donc l'état.
        this.#burstStart = 0;
        this.#burstCount = 0;
        this.#watch();
        this.refresh();
      }, 2000);
      return;
    }

    this.#scheduled = true;
    window.setTimeout(() => {
      this.#scheduled = false;
      if (this.isConnected) this.refresh();
    }, 0);
  }

  /* --- Données ---------------------------------------------------------------------------------- */

  #readTable() {
    const script = this.querySelector('script[data-pjc-variants]');
    const raw = script?.textContent ?? '';
    if (raw === this.#rawVariants && this.#optionNames.length) return;
    this.#rawVariants = raw;

    try {
      const parsed = JSON.parse(raw || '[]');
      this.#variants = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.#variants = [];
    }

    try {
      const names = JSON.parse(this.dataset.optionNames || '[]');
      this.#optionNames = Array.isArray(names) ? names.map((name) => String(name).trim()) : [];
    } catch {
      this.#optionNames = [];
    }
  }

  get #productAvailable() {
    return this.dataset.productAvailable === 'true';
  }

  get #currency() {
    return this.dataset.currency || window.Shopify?.currency?.active || 'CHF';
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '', this.#currency);
  }

  #form() {
    return /** @type {HTMLElement | null} */ (this.parentElement?.querySelector('product-form-component') ?? null);
  }

  #mainButton() {
    return /** @type {HTMLButtonElement | null} */ (this.#form()?.querySelector('[data-pjc-buy-button="main"]') ?? null);
  }

  #pickers() {
    return Array.from(this.#root.querySelectorAll('variant-picker')).filter(
      (picker) => !picker.closest('product-card, quick-add-dialog, dialog')
    );
  }

  /**
   * Un fieldset par option à plusieurs valeurs. P pose data-pjc-option-name / data-pjc-option-kind ;
   * à défaut, le nom et le type sont déduits du nom brut de l'option.
   */
  #fieldsets() {
    const result = [];
    for (const picker of this.#pickers()) {
      for (const element of picker.querySelectorAll('fieldset')) {
        const radios = /** @type {HTMLInputElement[]} */ (Array.from(element.querySelectorAll('input[type="radio"]')));
        if (radios.length === 0) continue;

        // Chantier Fiche : data-pjc-option-name = nom d'origine, data-pjc-option-label = libellé français,
        // data-pjc-option-kind = size | color | other. À défaut, déduits des radios.
        const rawName = (
          element.dataset.pjcOptionName ??
          radios[0].dataset.optionName ??
          element.querySelector('legend')?.textContent ??
          ''
        ).trim();
        const kind = guessKind(element.dataset.pjcOptionKind || rawName);
        const label = element.dataset.pjcOptionLabel?.trim() || frenchLabel(rawName, kind);
        result.push({
          element,
          radios,
          select: null,
          rawName,
          kind,
          label,
          gated: radios.length > 1 && kind !== 'color',
        });
      }

      // Variante « menus déroulants » : un select par option, traité comme un fieldset.
      for (const select of picker.querySelectorAll('select')) {
        const options = Array.from(select.options);
        if (options.length === 0) continue;

        const holder = /** @type {HTMLElement} */ (select.closest('.variant-option') ?? select);
        const rawName = (holder.dataset.pjcOptionName ?? options[0].dataset.optionName ?? '').trim();
        const kind = guessKind(holder.dataset.pjcOptionKind || rawName);
        const label = holder.dataset.pjcOptionLabel?.trim() || frenchLabel(rawName, kind);
        result.push({
          element: holder,
          radios: [],
          select,
          rawName,
          kind,
          label,
          gated: options.length > 1 && kind !== 'color',
        });
      }
    }
    return result;
  }

  /** @returns {Record<string, string>} */
  #selection() {
    /** @type {Record<string, string>} */
    const selection = {};
    for (const fieldset of this.#fieldsets()) {
      const checked = fieldset.radios.find((radio) => radio.checked);
      if (checked) selection[fieldset.rawName] = checked.value;
    }
    for (const picker of this.#pickers()) {
      for (const select of picker.querySelectorAll('select')) {
        const option = select.selectedOptions[0];
        const name = option?.dataset.optionName?.trim();
        if (name) selection[name] = option.value;
      }
    }
    return selection;
  }

  /**
   * @param {{o: string[]}} variant
   * @param {Record<string, string>} selection
   * @param {Set<string>} [ignore]
   */
  #fits(variant, selection, ignore = new Set()) {
    return this.#optionNames.every(
      (name, index) => ignore.has(name) || !(name in selection) || variant.o[index] === selection[name]
    );
  }

  /** Variante correspondant à la sélection (null : combinaison inexistante ou table absente). */
  #currentVariant() {
    if (this.#variants.length === 0) return this.#fallbackVariant();
    const selection = this.#selection();
    return this.#variants.find((variant) => this.#fits(variant, selection)) ?? null;
  }

  /** Sans table (plus de 250 variantes) : dernière variante renvoyée par le sélecteur ou JSON initial. */
  #fallbackVariant() {
    let source = this.#resource;
    if (!source) {
      const raw = this.#pickers()[0]?.querySelector('script[type="application/json"]')?.textContent;
      try {
        source = raw ? JSON.parse(raw) : null;
      } catch {
        source = null;
      }
    }
    if (!source || !source.id) return null;
    return {
      id: Number(source.id),
      a: source.available !== false,
      p: toCents(source.price),
      c: toCents(source.compare_at_price),
      o: [],
      t: String(source.title || ''),
    };
  }

  #quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.#form()?.querySelector('input[name="quantity"]') ?? null);
    const value = Math.floor(Number(input?.value));
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /* --- État d'achat ----------------------------------------------------------------------------- */

  #computeSingle() {
    const fieldsets = this.#fieldsets();
    const missingSets = fieldsets.filter((fieldset) => fieldset.gated && !this.#confirmed.has(fieldset.rawName));
    const missing = missingSets.map((fieldset) => fieldset.label);
    const variant = this.#currentVariant();
    const quantity = this.#quantity();

    let unit = variant?.p ?? 0;
    let compare = variant && variant.c > variant.p ? variant.c : 0;
    let fromPrice = false;
    this.#choosable = this.#productAvailable;

    if (missingSets.length && this.#variants.length) {
      const ignore = new Set(missingSets.map((fieldset) => fieldset.rawName));
      const selection = this.#selection();
      const candidates = this.#variants.filter((candidate) => candidate.a && this.#fits(candidate, selection, ignore));
      this.#choosable = this.#choosable && candidates.length > 0;
      if (candidates.length) {
        const prices = candidates.map((candidate) => candidate.p);
        const min = Math.min(...prices);
        fromPrice = min !== Math.max(...prices);
        unit = min;
        const cheapest = candidates.find((candidate) => candidate.p === min);
        compare = !fromPrice && cheapest && cheapest.c > cheapest.p ? cheapest.c : 0;
      }
    }

    const available = Boolean(variant?.a) && this.#productAvailable;
    const priceCents = unit * quantity;
    const compareAtCents = compare > unit ? compare * quantity : null;

    return {
      productId: String(this.dataset.productId || ''),
      mode: 'single',
      quantity,
      items: [{ variantId: variant ? String(variant.id) : '', available, title: variant?.t ?? '' }],
      missing,
      priceCents,
      compareAtCents,
      savingsCents: compareAtCents ? compareAtCents - priceCents : 0,
      currency: this.#currency,
      ready: missing.length === 0 && available,
      fromPrice,
      unitPriceCents: unit,
    };
  }

  /** Normalise l'état envoyé par le chantier Offres (mode duo / trio). */
  #computeOffer() {
    const offer = this.#offer;
    const items = Array.isArray(offer.items)
      ? offer.items.map((item) => ({
          variantId: String(item?.variantId ?? ''),
          available: item?.available !== false,
          title: String(item?.title ?? ''),
        }))
      : [];
    const missing = Array.isArray(offer.missing) ? offer.missing.map(String) : [];
    // QA : tant qu'un article n'est pas choisi, le prix du lot n'est qu'un « à partir de » (variante la moins chère) :
    // pas de prix barré ni d'économie affirmés dans la barre fixe.
    const from = missing.length > 0;
    const priceCents = toCents(offer.priceCents);
    const compareAtCents = from ? null : toCents(offer.compareAtCents) || null;

    return {
      productId: String(offer.productId ?? this.dataset.productId ?? ''),
      mode: String(offer.mode),
      quantity: Math.max(1, Math.floor(Number(offer.quantity)) || items.length || 1),
      items,
      missing,
      priceCents,
      compareAtCents,
      savingsCents: from ? 0 : toCents(offer.savingsCents),
      currency: String(offer.currency || this.#currency),
      ready: Boolean(offer.ready) && missing.length === 0 && items.every((item) => item.available),
      fromPrice: from && priceCents > 0,
      unitPriceCents: 0,
    };
  }

  /** Libellés et prix déjà formatés, consommés par la barre fixe et le bouton principal. */
  #decorate(purchase) {
    const packName = purchase.mode === 'trio' ? 'Trio' : purchase.mode === 'duo' ? 'Duo' : '';
    const pack = purchase.mode !== 'single';
    const priceText = purchase.priceCents > 0 ? this.#money(purchase.priceCents) : '';
    const unavailable = !purchase.ready && purchase.missing.length === 0;
    const hasVariant = Boolean(purchase.items[0]?.variantId);

    let main = TEXT.add;
    let sticky = TEXT.add;

    if (pack) {
      main =
        purchase.missing.length || !purchase.ready || !priceText
          ? `Ajouter le pack ${packName}`
          : `Ajouter le pack ${packName} · ${priceText}`;
      sticky = purchase.missing.length
        ? `Choisir ${phrase(purchase.missing[0])}`
        : unavailable
        ? TEXT.unavailable
        : `Ajouter le pack ${packName}${priceText ? ` · ${priceText}` : ''}`;
    } else if (purchase.missing.length) {
      sticky = `Choisir ${phrase(purchase.missing[0])}`;
    } else if (unavailable) {
      main = hasVariant ? TEXT.unavailable : TEXT.chooseVariant;
      sticky = main;
    } else if (purchase.quantity > 1) {
      sticky = `${TEXT.add} (${purchase.quantity})`;
    }

    return {
      ...purchase,
      priceText,
      compareText: purchase.compareAtCents ? this.#money(purchase.compareAtCents) : '',
      cta: { main, sticky },
    };
  }

  refresh() {
    if (!this.#initialised) return;
    this.#readTable();

    const raw = this.#offer ? this.#computeOffer() : this.#computeSingle();
    const next = this.#decorate(raw);
    const key = JSON.stringify(next);
    const changed = key !== this.#lastKey;

    // Une consigne « Choisissez… » devient fausse dès que la liste des choix manquants change.
    const missingKey = `${next.mode}|${next.missing.join('|')}`;
    if (missingKey !== this.#lastMissing && this.#messageKind === 'notice') this.#clearMessage();
    this.#lastMissing = missingKey;

    this.#lastKey = key;
    window.PJC_PURCHASE = next;
    this.#paint(next);

    if (changed) {
      document.dispatchEvent(new CustomEvent(EVENTS.change, { detail: next }));
    }
  }

  /* --- Rendu (idempotent : n'écrit que ce qui diffère) ------------------------------------------ */

  /**
   * @param {Element} element
   * @param {string} name
   * @param {string | null} value
   */
  #setAttr(element, name, value) {
    if (value === null) {
      if (element.hasAttribute(name)) element.removeAttribute(name);
    } else if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  }

  #paint(purchase) {
    this.#paintFieldsets();
    this.#paintMainButton(purchase);
    this.#paintAccelerated(purchase);
  }

  #paintFieldsets() {
    const fieldsets = this.#fieldsets();
    for (const fieldset of fieldsets) {
      const unconfirmed = fieldset.gated && !this.#confirmed.has(fieldset.rawName);
      this.#setAttr(fieldset.element, 'data-pjc-unconfirmed', unconfirmed ? '' : null);
    }

    const component = this.closest('product-component');
    if (component) {
      const sizes = fieldsets.filter((fieldset) => fieldset.kind === 'size' && fieldset.gated);
      const confirmed = sizes.length > 0 && sizes.every((fieldset) => this.#confirmed.has(fieldset.rawName));
      this.#setAttr(component, 'data-pjc-size-confirmed', confirmed ? 'true' : null);
      // Tant qu'un choix manque, l'état de la variante provisoire ne doit pas être présenté comme celui de l'achat.
      const choiceMissing = fieldsets.some((fieldset) => fieldset.gated && !this.#confirmed.has(fieldset.rawName));
      this.#setAttr(component, 'data-pjc-choice-missing', choiceMissing ? '' : null);
    }
  }

  #paintMainButton(purchase) {
    const button = this.#mainButton();
    if (!button) return;

    const container = button.closest('add-to-cart-component');
    if (container) this.#setAttr(container, 'data-pjc-state', this.#state);
    this.#setAttr(button, 'aria-busy', this.#state === 'loading' ? 'true' : null);
    // Pas d'éclat de fête (pjc-fx.js) tant que le clic n'ajoutera rien (choix manquant, indisponible).
    this.#setAttr(button, 'data-pjc-no-sparkle', purchase.ready ? null : '');

    // Choix manquant alors que la variante provisoire (la première de la liste) est épuisée : le rendu serveur a
    // désactivé le bouton à tort. Tant qu'une combinaison reste disponible, il garde son libellé d'achat et reste
    // actif ; au clic, #guide() mène au choix manquant.
    const guiding = purchase.mode === 'single' && purchase.missing.length > 0 && this.#choosable;
    if (guiding && button.disabled) button.disabled = false;

    const label = button.querySelector('.pjc-btn__label');
    if (label) {
      const fallback = label.getAttribute('data-pjc-label') || TEXT.add;
      // Libellé du serveur en achat simple ; libellé calculé pour un pack, un choix manquant ou une sélection indisponible.
      const computed = purchase.mode !== 'single' || guiding || (!purchase.ready && purchase.missing.length === 0);
      const wanted = computed ? purchase.cta.main : fallback;
      if (label.textContent !== wanted) label.textContent = wanted;
    }

    const errorSlot = this.#errorSlot();
    this.#setAttr(button, 'aria-describedby', errorSlot && !errorSlot.classList.contains('hidden') ? errorSlot.id : null);
  }

  #paintAccelerated(purchase) {
    const root = this.#form()?.querySelector('.accelerated-checkout-block');
    const buttons = root?.querySelector('[data-pjc-accel-buttons]');
    const note = /** @type {HTMLElement | null} */ (root?.querySelector('[data-pjc-accel-note]') ?? null);
    if (!buttons || !note) return;

    const pack = purchase.mode !== 'single';
    const blocked = pack || purchase.missing.length > 0;
    const reason = pack ? TEXT.packNoExpress : `${chooseMessage(purchase.missing)} pour activer le paiement express.`;

    this.#setAttr(buttons, 'data-pjc-disabled', blocked ? '' : null);
    this.#setAttr(buttons, 'aria-disabled', blocked ? 'true' : null);
    this.#setAttr(buttons, 'inert', blocked ? '' : null);
    if (note.hidden === blocked) note.hidden = !blocked;
    if (blocked && note.textContent !== reason) note.textContent = reason;
  }

  #errorSlot() {
    return /** @type {HTMLElement | null} */ (this.#form()?.querySelector('[ref="addToCartTextError"]') ?? null);
  }

  /* --- Événements de sélection ------------------------------------------------------------------- */

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    const isRadio = target instanceof HTMLInputElement && target.type === 'radio';
    if (!isRadio && !(target instanceof HTMLSelectElement)) return;
    if (!target.closest('variant-picker') || target.closest('product-card, quick-add-dialog, dialog')) return;

    const fieldset = this.#fieldsets().find((candidate) =>
      isRadio ? candidate.radios.includes(/** @type {HTMLInputElement} */ (target)) : candidate.select === target
    );
    // Seul un choix de l'utilisateur confirme une option : événement natif, ou changement programmatique
    // survenu juste après un geste (activation utilisateur en cours). Jamais un changement au chargement.
    const userDriven = event.isTrusted || Boolean(navigator.userActivation?.isActive);
    if (fieldset && userDriven) this.#confirmed.add(fieldset.rawName);

    this.#clearMessage();
    this.#syncVariantId();
    this.refresh();
  };

  /** @param {Event} event */
  #onQuantity = (event) => {
    const target = event.target;
    if (event.type === 'input' && !(target instanceof HTMLInputElement && target.name === 'quantity')) return;
    this.#schedule();
  };

  /** @param {any} event */
  #onProductSelect = (event) => {
    event.promise
      ?.then((result) => {
        const resource = result?.detail?.resource;
        if (resource && resource.id) {
          this.#resource = resource;
          this.refresh();
        }
      })
      .catch(() => {});
  };

  /** Met le champ id à jour tout de suite, sans attendre la requête de section (achat accéléré fiable). */
  #syncVariantId() {
    if (this.#variants.length === 0) return;
    const variant = this.#currentVariant();
    const input = /** @type {HTMLInputElement | null} */ (this.#form()?.querySelector('input[name="id"]') ?? null);
    if (!variant || !input || input.value === String(variant.id)) return;

    input.value = String(variant.id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {any} event */
  #onOffer = (event) => {
    const detail = event.detail;
    this.#offer = detail && detail.mode && detail.mode !== 'single' ? detail : null;
    this.refresh();
  };

  /** @param {any} event */
  #onLookup = (event) => {
    const detail = event.detail;
    if (!detail?.form || !this.parentElement?.contains(detail.form) || this.#variants.length === 0) return;

    const variant = this.#currentVariant();
    detail.variant = variant ? { id: String(variant.id), available: variant.a } : null;
  };

  /* --- Soumission ------------------------------------------------------------------------------- */

  /** @param {any} event */
  #onSubmit = (event) => {
    const { source, form } = event.detail ?? {};

    if (source === 'main' && (!form || !this.parentElement?.contains(form))) return;
    event.detail.handled = true;

    const origin = source === 'sticky' ? 'sticky' : this.#pendingSource ?? 'main';
    this.#pendingSource = null;

    if (this.#state === 'loading') {
      event.preventDefault();
      return;
    }

    const purchase = window.PJC_PURCHASE;
    if (!purchase) return;

    if (purchase.missing.length) {
      event.preventDefault();
      this.#guide(origin);
      return;
    }

    if (purchase.mode !== 'single') {
      event.preventDefault();
      this.#submitPack(origin);
      return;
    }

    if (!purchase.ready) {
      event.preventDefault();
      return;
    }

    this.#source = origin;

    // Barre fixe : le bouton principal exécute l'ajout via le formulaire du thème.
    if (source === 'sticky') {
      event.preventDefault();
      const button = this.#mainButton();
      if (!button || button.disabled) {
        this.#fail(TEXT.generic);
        return;
      }
      this.#pendingSource = 'sticky';
      button.click();
      this.#pendingSource = null;
    }
  };

  /** Rapports de product-form.js (chargement / succès / erreur de l'ajout simple). */
  /** @param {any} event */
  #onReport = (event) => {
    const detail = event.detail;
    if (!detail || detail.broadcast || detail.handled || !detail.form) return;
    if (!this.parentElement?.contains(detail.form)) return;

    detail.handled = true;
    detail.skipFly = this.#source === 'sticky';
    this.#setState(detail.state, detail.message);
  };

  async #submitPack(origin) {
    const purchase = window.PJC_PURCHASE;
    const offers = window.PJC_OFFERS;
    this.#source = origin;

    if (!purchase.ready || !offers || typeof offers.add !== 'function') {
      this.#fail(TEXT.packUnavailable);
      return;
    }

    this.#setState('loading');

    /** @type {{ok?: boolean, message?: string} | null} */
    let result;
    try {
      // Pas de course contre une minuterie : offers.add() gère ses propres délais (requêtes annulables, relecture du
      // panier en cas de timeout) et se termine toujours. Annoncer un échec pendant que la requête court encore
      // laisserait croire à tort que rien n'a été ajouté.
      result = await offers.add();
    } catch {
      result = { ok: false, message: TEXT.network };
    }

    if (!result || typeof result !== 'object') result = { ok: false, message: TEXT.generic };

    // L'ajout de pack prévient lui-même le tiroir panier du thème (pjc-offers.js) : rien à émettre ici.
    if (result.ok) {
      this.#setState('success');
    } else {
      this.#setState('error', typeof result.message === 'string' && result.message ? result.message : TEXT.generic);
    }
  }

  /* --- États (chargement, succès, erreur) ------------------------------------------------------- */

  /**
   * @param {'loading' | 'success' | 'error'} state
   * @param {string} [message]
   */
  #setState(state, message = '') {
    window.clearTimeout(this.#successTimer);

    if (state === 'error') {
      this.#fail(message || TEXT.generic);
      return;
    }

    if (state === 'loading') {
      if (this.#state === 'loading') return;
      this.#state = 'loading';
      this.#clearMessage();
      announce(TEXT.loading, 'polite');
    } else {
      this.#state = 'success';
      this.#clearMessage();
      announce(TEXT.added, 'polite');
      this.#successTimer = window.setTimeout(() => {
        this.#state = 'idle';
        this.#broadcast('idle');
        this.#paint(window.PJC_PURCHASE);
      }, SUCCESS_DURATION);
      document.dispatchEvent(
        new CustomEvent(EVENTS.cartUpdated, {
          detail: {
            source: this.#source,
            mode: window.PJC_PURCHASE?.mode ?? 'single',
            items: window.PJC_PURCHASE?.items ?? [],
          },
        })
      );
    }

    this.#broadcast(state);
    this.#paint(window.PJC_PURCHASE);
  }

  /** @param {string} message */
  #fail(message) {
    this.#state = 'idle';
    this.#showMessage(message, 'error');
    this.#paint(window.PJC_PURCHASE);
  }

  /**
   * @param {string} state
   * @param {string} [message]
   */
  #broadcast(state, message = '') {
    document.dispatchEvent(
      new CustomEvent(EVENTS.state, {
        detail: {
          broadcast: true,
          handled: true,
          state,
          message,
          source: this.#source,
          mode: window.PJC_PURCHASE?.mode ?? 'single',
        },
      })
    );
  }

  /**
   * Message visible près du bouton (et dans la barre fixe via diffusion) + annonce vocale.
   * @param {string} message
   * @param {'error' | 'notice'} kind
   */
  #showMessage(message, kind) {
    window.clearTimeout(this.#messageTimer);
    this.#message = message;
    this.#messageKind = kind;

    const slot = this.#errorSlot();
    const text = slot?.querySelector('.pjc-buy-error__text');
    if (slot && text) {
      text.textContent = message;
      slot.dataset.kind = kind;
      slot.classList.remove('hidden');
    }

    announce(message, 'assertive');
    this.#broadcast(kind, message);

    const sticky = document.querySelector('.pjc-sticky__bar[data-stuck="true"]');
    if (!inViewport(slot) && !sticky) showToast(message, kind);

    this.#messageTimer = window.setTimeout(() => this.#clearMessage(), MESSAGE_DURATION);
  }

  #clearMessage() {
    window.clearTimeout(this.#messageTimer);
    if (!this.#message) return;
    this.#message = '';

    const slot = this.#errorSlot();
    slot?.classList.add('hidden');
    this.#broadcast('clear');
    this.#paintMainButton(window.PJC_PURCHASE);
  }

  /* --- Choix manquant : défilement, focus, mise en évidence, annonce ------------------------------ */

  #guide(origin) {
    this.#source = origin;
    const purchase = window.PJC_PURCHASE;

    /** @type {HTMLElement | null} */
    let target = null;
    /** @type {HTMLElement | null} */
    let focusable = null;
    const message = chooseMessage(purchase.missing);

    if (purchase.mode === 'single') {
      const fieldset = this.#fieldsets().find((candidate) => purchase.missing.includes(candidate.label));
      target = fieldset?.element ?? null;
      focusable = fieldset
        ? fieldset.radios.find((radio) => radio.checked) ?? fieldset.radios[0] ?? fieldset.select
        : null;
    } else {
      // Pack : le sélecteur d'articles appartient au composant d'offres, qui défile, met le focus sur
      // l'article incomplet et fournit le message (PJC_OFFERS.focusMissing).
      const offerMessage = typeof window.PJC_OFFERS?.focusMissing === 'function' ? window.PJC_OFFERS.focusMissing() : '';
      if (offerMessage) {
        this.#showMessage(offerMessage, 'notice');
        return;
      }
    }

    this.#showMessage(message, 'notice');
    if (!target) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    focusable?.focus({ preventScroll: true });

    const highlighted = target;
    window.clearTimeout(this.#highlightTimer);
    this.#setAttr(highlighted, 'data-pjc-highlight', '');
    this.#highlightTimer = window.setTimeout(() => {
      highlighted.removeAttribute('data-pjc-highlight');
    }, HIGHLIGHT_DURATION);
  }
}

if (!customElements.get('pjc-buy')) {
  customElements.define('pjc-buy', PjcBuy);
}
